import React, { useEffect, useMemo, useState } from "react";
import api, { API_BASE, CLP, formatApiErrorDetail } from "@/lib/api";
import { PageContainer, PageHeader, PrimaryButton, SecondaryButton, EmptyState, Field, inputCls } from "@/components/ui-kit";
import { toast } from "sonner";
import { Package, Plus, FileDown, Pencil, Trash2, X, Image as ImgIcon, Eye, EyeOff } from "lucide-react";

const CATEGORIES = ["Cardio", "Fuerza", "Accesorios", "Repuestos", "Servicios"];

function ProductModal({ initial, onClose, onSaved }) {
  const [data, setData] = useState(
    initial || { name: "", description: "", category: "Cardio", photo_url: "", price: "", active: true }
  );
  const [saving, setSaving] = useState(false);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen excede 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setData({ ...data, photo_url: reader.result });
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: data.name,
        description: data.description,
        category: data.category,
        photo_url: data.photo_url || "",
        price: data.price === "" || data.price === null ? null : Number(data.price),
        active: data.active,
      };
      if (initial?.id) {
        await api.patch(`/products/${initial.id}`, payload);
      } else {
        await api.post("/products", payload);
      }
      toast.success(initial?.id ? "Producto actualizado" : "Producto creado");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-xl border-t-4 border-[#FF5A00] max-h-[90vh] overflow-y-auto"
        data-testid="product-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <div className="font-display font-bold text-xl">{initial?.id ? "Editar producto" : "Nuevo producto"}</div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 grid grid-cols-1 gap-4">
          <Field label="Nombre *">
            <input required data-testid="product-name-input" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Categoría">
            <select data-testid="product-category-select" value={data.category} onChange={(e) => setData({ ...data, category: e.target.value })} className={inputCls}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Descripción">
            <textarea
              data-testid="product-description-input"
              rows={3}
              value={data.description}
              onChange={(e) => setData({ ...data, description: e.target.value })}
              className={inputCls}
              placeholder="Detalle técnico, dimensiones, garantía…"
            />
          </Field>
          <Field label="Precio neto (CLP, opcional)">
            <input
              type="number"
              min="0"
              data-testid="product-price-input"
              value={data.price ?? ""}
              onChange={(e) => setData({ ...data, price: e.target.value })}
              className={inputCls}
              placeholder="Ej: 850000"
            />
          </Field>
          <Field label="Foto del producto">
            <div className="flex items-start gap-4">
              {data.photo_url ? (
                <div className="relative">
                  <img src={data.photo_url} alt="" className="w-28 h-28 object-cover border border-neutral-300" />
                  <button
                    type="button"
                    onClick={() => setData({ ...data, photo_url: "" })}
                    className="absolute -top-2 -right-2 bg-white border border-neutral-300 p-1 hover:bg-neutral-100"
                  ><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <div className="w-28 h-28 border border-dashed border-neutral-300 flex items-center justify-center text-neutral-400">
                  <ImgIcon className="w-6 h-6" />
                </div>
              )}
              <label className="cursor-pointer text-xs font-mono-kpi uppercase tracking-[0.15em] text-neutral-700 hover:text-[#FF5A00]">
                <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="product-photo-input" />
                Subir foto
              </label>
            </div>
          </Field>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={data.active} onChange={(e) => setData({ ...data, active: e.target.checked })} className="w-4 h-4" />
            Visible en catálogo
          </label>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50 sticky bottom-0">
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={saving} data-testid="product-submit">{saving ? "Guardando…" : "Guardar producto"}</PrimaryButton>
        </div>
      </form>
    </div>
  );
}

function CatalogPdfModal({ products, onClose }) {
  const initialMap = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p.price != null && p.price > 0])),
    [products]
  );
  const [showPrices, setShowPrices] = useState(initialMap);
  const [title, setTitle] = useState("Catálogo de productos");
  const [intro, setIntro] = useState(
    "Equipamiento, repuestos y servicios disponibles para tu instalación. Precios netos en CLP (sin IVA)."
  );
  const [downloading, setDownloading] = useState(false);

  const toggleAll = (val) =>
    setShowPrices(Object.fromEntries(products.map((p) => [p.id, val])));

  const download = async () => {
    setDownloading(true);
    try {
      const resp = await fetch(`${API_BASE}/catalog/pdf`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          show_prices: showPrices,
          title,
          intro,
          product_ids: products.map((p) => p.id),
        }),
      });
      if (!resp.ok) throw new Error("Error generando PDF");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Catalogo_AllActive_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Catálogo PDF descargado");
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl border-t-4 border-[#FF5A00] max-h-[90vh] overflow-y-auto" data-testid="catalog-pdf-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <FileDown className="w-4 h-4 text-[#FF5A00]" />
            <div className="font-display font-bold text-xl">Generar catálogo PDF</div>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-5">
          <Field label="Título del catálogo">
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="catalog-title-input" />
          </Field>
          <Field label="Texto introductorio">
            <textarea rows={2} value={intro} onChange={(e) => setIntro(e.target.value)} className={inputCls} />
          </Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                Mostrar precio por producto
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => toggleAll(true)} className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] px-2 py-1 border border-neutral-300 hover:border-[#FF5A00] hover:text-[#FF5A00]">
                  Mostrar todos
                </button>
                <button type="button" onClick={() => toggleAll(false)} className="font-mono-kpi text-[9px] uppercase tracking-[0.15em] px-2 py-1 border border-neutral-300 hover:border-[#FF5A00] hover:text-[#FF5A00]">
                  Ocultar todos
                </button>
              </div>
            </div>
            <div className="border border-neutral-200 divide-y divide-neutral-100 max-h-72 overflow-y-auto">
              {products.map((p) => (
                <label key={p.id} className="flex items-center justify-between px-4 py-2.5 text-sm hover:bg-neutral-50 cursor-pointer">
                  <div className="flex items-center gap-3 min-w-0">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt="" className="w-9 h-9 object-cover border border-neutral-200 flex-shrink-0" />
                    ) : (
                      <div className="w-9 h-9 bg-neutral-100 border border-neutral-200 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="font-medium truncate">{p.name}</div>
                      <div className="text-xs text-neutral-500">{p.price ? CLP(p.price) : "Sin precio"}</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={!!showPrices[p.id]}
                    onChange={(e) => setShowPrices({ ...showPrices, [p.id]: e.target.checked })}
                    className="w-4 h-4 flex-shrink-0"
                    data-testid={`catalog-price-toggle-${p.id}`}
                  />
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50 sticky bottom-0">
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={download} disabled={downloading} data-testid="catalog-pdf-download">
            {downloading ? "Generando…" : "Descargar PDF"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default function CatalogPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Eliminar producto "${p.name}"?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Producto eliminado");
    load();
  };

  const filtered = filter === "all" ? products : products.filter((p) => p.category === filter);

  return (
    <PageContainer>
      <PageHeader
        kicker={`${products.length} productos`}
        title="Catálogo"
        subtitle="Equipamiento, repuestos y servicios disponibles. Genera un PDF descargable para enviar a clientes."
        actions={
          <>
            <SecondaryButton
              data-testid="catalog-generate-pdf"
              onClick={() => setShowPdf(true)}
              disabled={products.length === 0}
            >
              <FileDown className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />
              Generar PDF
            </SecondaryButton>
            <PrimaryButton data-testid="catalog-new-product" onClick={() => setShowNew(true)}>
              <Plus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />
              Nuevo producto
            </PrimaryButton>
          </>
        }
      />

      {/* Category filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button
          onClick={() => setFilter("all")}
          className={`font-mono-kpi text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${
            filter === "all" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-neutral-300 hover:border-[#FF5A00]"
          }`}
        >
          Todos · {products.length}
        </button>
        {CATEGORIES.map((cat) => {
          const count = products.filter((p) => p.category === cat).length;
          if (count === 0) return null;
          return (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`catalog-filter-${cat}`}
              className={`font-mono-kpi text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${
                filter === cat ? "bg-[#FF5A00] text-white border-[#FF5A00]" : "bg-white border-neutral-300 hover:border-[#FF5A00]"
              }`}
            >
              {cat} · {count}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="font-mono-kpi text-xs text-neutral-500 uppercase tracking-[0.2em]">Cargando…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Sin productos"
          description="Agrega tu primer producto para comenzar a construir el catálogo de All Active."
          action={<PrimaryButton onClick={() => setShowNew(true)}>Nuevo producto</PrimaryButton>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="catalog-grid">
          {filtered.map((p) => (
            <div
              key={p.id}
              data-testid={`catalog-card-${p.id}`}
              className="bg-white border border-neutral-200 flex flex-col group hover:border-[#FF5A00] transition-colors"
            >
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.name} className="w-full h-44 object-cover" />
              ) : (
                <div className="w-full h-44 bg-neutral-50 flex items-center justify-center text-neutral-300">
                  <Package className="w-12 h-12" strokeWidth={1.2} />
                </div>
              )}
              <div className="p-4 flex-1 flex flex-col">
                {p.category && (
                  <div className="font-mono-kpi text-[9px] uppercase tracking-[0.2em] text-[#FF5A00] mb-1">{p.category}</div>
                )}
                <div className="font-display font-bold text-base leading-snug mb-1">{p.name}</div>
                <div className="text-xs text-neutral-500 line-clamp-3 flex-1">{p.description}</div>
                <div className="mt-3 flex items-center justify-between border-t border-neutral-100 pt-3">
                  <div className="font-mono-kpi text-sm font-bold">
                    {p.price ? CLP(p.price) : <span className="text-neutral-400 text-xs">Sin precio</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`catalog-edit-${p.id}`}
                      onClick={() => setEditing(p)}
                      className="p-1.5 hover:bg-neutral-100 text-neutral-500 hover:text-[#FF5A00]"
                      title="Editar"
                    ><Pencil className="w-3.5 h-3.5" /></button>
                    <button
                      data-testid={`catalog-delete-${p.id}`}
                      onClick={() => remove(p)}
                      className="p-1.5 hover:bg-[#D50000]/10 text-neutral-500 hover:text-[#D50000]"
                      title="Eliminar"
                    ><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showNew || editing) && (
        <ProductModal
          initial={editing}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
      {showPdf && (
        <CatalogPdfModal products={products} onClose={() => setShowPdf(false)} />
      )}
    </PageContainer>
  );
}
